import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Avatar } from "@/components/common";
import { roleLabel } from "@/lib/format";
import { APP_VERSION } from "@/lib/version";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronRight, User, LogOut, ShieldCheck, Building2, Sparkles, Bell, Loader2 } from "@/components/icons";
import {
  enablePush, disablePush, sendTestPush, statusPush,
  isSupported, isIOS, isStandalone, permissionStatus,
  getSomSinoAtivo, setSomSinoAtivo,
} from "@/lib/webpush";

function Row({ icon: Icon, label, sub, onClick, danger }) {
  return (
    <button onClick={onClick} className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors text-left" data-testid={`cfg-${label}`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${danger ? "bg-bad-soft text-bad" : "bg-slate-100 text-navy"}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium ${danger ? "text-bad" : "text-navy"}`}>{label}</div>
        {sub && <div className="text-xs text-muted-foreground truncate">{sub}</div>}
      </div>
      <ChevronRight className="w-4 h-4 text-slate-300" />
    </button>
  );
}

// Preferências pessoais — hoje: apenas o resumo do dia. Mantém a intenção
// da "aba Preferências" solicitada, mas em bloco simples para não poluir a
// tela. Se surgir uma segunda preferência, dá para agrupar visualmente aqui.
function Preferencias() {
  const [mostrar, setMostrar] = useState(null); // null = carregando
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    api.get("/resumo-dia")
      .then((r) => setMostrar(r.data?.mostrar_resumo_dia !== false))
      .catch(() => setMostrar(true));
  }, []);

  const toggle = async () => {
    if (mostrar === null) return;
    const novo = !mostrar;
    setSalvando(true);
    setMostrar(novo);
    try {
      await api.post("/resumo-dia/preferencia", { mostrar_resumo_dia: novo });
      toast.success(novo ? "Ativado" : "Desativado");
    } catch {
      setMostrar(!novo);
      toast.error("Não foi possível salvar");
    } finally {
      setSalvando(false);
    }
  };

  const ativo = !!mostrar;

  return (
    <div className="bg-card rounded-2xl border border-black/5 overflow-hidden shadow-[0_4px_20px_rgb(0,0,0,0.03)] mb-4" data-testid="cfg-preferencias">
      <div className="px-5 pt-4 pb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Preferências</div>
      <div className="flex items-center gap-3 p-4 border-t border-black/5">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-slate-100 text-navy">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-navy">Mostrar resumo do dia ao entrar</div>
          <div className="text-xs text-muted-foreground">Aparece uma vez por dia com o que pede atenção</div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={ativo}
          disabled={mostrar === null || salvando}
          onClick={toggle}
          data-testid="cfg-toggle-resumo-dia"
          className={`w-12 h-7 rounded-full relative transition-colors ${ativo ? "bg-brand" : "bg-slate-300"} disabled:opacity-60`}
        >
          <span
            className={`absolute top-0.5 h-6 w-6 bg-white rounded-full shadow transition-all ${ativo ? "left-[calc(100%-1.625rem)]" : "left-0.5"}`}
          />
        </button>
      </div>
    </div>
  );
}

// NOTIF-1 — seção "Notificações no celular". Pede permissão só ao tocar
// no botão (exigência do iPhone), mostra passo a passo para iOS fora do
// standalone, botão "Enviar teste" e preferência de som do sino (local).
function NotificacoesCelular() {
  const [perm, setPerm] = useState(permissionStatus());
  const [inscritos, setInscritos] = useState(0);
  const [busy, setBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [somAtivo, setSomAtivoState] = useState(getSomSinoAtivo());
  const supported = isSupported();
  const ios = isIOS();
  const standalone = isStandalone();
  const precisaPWA = ios && !standalone;

  useEffect(() => {
    (async () => {
      const s = await statusPush();
      setInscritos(s.inscritos || 0);
    })();
  }, [perm]);

  const ativar = async () => {
    setBusy(true);
    try {
      await enablePush();
      setPerm(permissionStatus());
      const s = await statusPush();
      setInscritos(s.inscritos || 0);
      toast.success("Notificações ativadas neste aparelho");
    } catch (err) {
      toast.error(err?.message || "Não foi possível ativar");
      setPerm(permissionStatus());
    } finally { setBusy(false); }
  };
  const desativar = async () => {
    setBusy(true);
    try {
      await disablePush();
      const s = await statusPush();
      setInscritos(s.inscritos || 0);
      toast.success("Notificações desativadas neste aparelho");
    } catch { toast.error("Não foi possível desativar"); }
    finally { setBusy(false); }
  };
  const testar = async () => {
    setTestBusy(true);
    try {
      await sendTestPush();
      toast.success("Notificação de teste enviada");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Falhou";
      toast.error(msg);
    } finally { setTestBusy(false); }
  };
  const toggleSom = () => {
    const novo = !somAtivo;
    setSomSinoAtivo(novo);
    setSomAtivoState(novo);
    toast.success(novo ? "Som do sino ativado" : "Som do sino desligado");
  };

  // Status textual.
  let statusLabel = "Não ativado neste aparelho";
  let statusColor = "text-slate-500";
  if (!supported) { statusLabel = "Este aparelho não suporta notificações"; statusColor = "text-bad"; }
  else if (perm === "denied") { statusLabel = "Bloqueado pelo navegador"; statusColor = "text-bad"; }
  else if (perm === "granted" && inscritos > 0) {
    statusLabel = `Ativado · ${inscritos} ${inscritos === 1 ? "aparelho" : "aparelhos"} inscritos`;
    statusColor = "text-ok";
  }

  return (
    <div className="bg-card rounded-2xl border border-black/5 overflow-hidden shadow-[0_4px_20px_rgb(0,0,0,0.03)] mb-4" data-testid="cfg-notif-celular">
      <div className="px-5 pt-4 pb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Notificações no celular</div>
      <div className="p-4 border-t border-black/5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-slate-100 text-navy shrink-0">
            <Bell className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-navy">Aviso com som no celular, mesmo com o CRM fechado</div>
            <div className={`text-xs mt-0.5 ${statusColor}`} data-testid="cfg-notif-status">{statusLabel}</div>
          </div>
        </div>

        {precisaPWA && (
          <div className="rounded-lg border border-warn/40 bg-warn-soft p-3 text-xs text-navy" data-testid="cfg-notif-ios-step">
            <b>No iPhone:</b> abra pelo Safari, toque em <b>Compartilhar</b> → <b>Adicionar à Tela de Início</b>,
            abra o CRM pelo ícone e toque em <b>Ativar notificações</b>.
          </div>
        )}

        {supported && !precisaPWA && (
          <div className="flex flex-wrap gap-2" data-testid="cfg-notif-botoes">
            {perm === "granted" && inscritos > 0 ? (
              <>
                <button
                  type="button"
                  disabled={testBusy}
                  onClick={testar}
                  data-testid="cfg-notif-testar"
                  className="h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-60 flex items-center gap-2"
                >
                  {testBusy && <Loader2 className="w-4 h-4 animate-spin" />} Enviar notificação de teste
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={desativar}
                  data-testid="cfg-notif-desativar"
                  className="h-10 px-4 rounded-lg border border-input text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60 flex items-center gap-2"
                >
                  Desativar neste aparelho
                </button>
              </>
            ) : (
              <button
                type="button"
                disabled={busy || perm === "denied"}
                onClick={ativar}
                data-testid="cfg-notif-ativar"
                className="h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-60 flex items-center gap-2"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Ativar notificações
              </button>
            )}
          </div>
        )}
        {perm === "denied" && (
          <div className="text-xs text-muted-foreground" data-testid="cfg-notif-denied-hint">
            Para liberar, abra as configurações do navegador e permita notificações para o site do CRM.
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 p-4 border-t border-black/5">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-slate-100 text-navy">
          <Bell className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-navy">Tocar som no sino quando chegar notificação</div>
          <div className="text-xs text-muted-foreground">Som curto só com o CRM aberto nesta aba</div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={somAtivo}
          onClick={toggleSom}
          data-testid="cfg-notif-som"
          className={`w-12 h-7 rounded-full relative transition-colors ${somAtivo ? "bg-brand" : "bg-slate-300"}`}
        >
          <span className={`absolute top-0.5 h-6 w-6 bg-white rounded-full shadow transition-all ${somAtivo ? "left-[calc(100%-1.625rem)]" : "left-0.5"}`} />
        </button>
      </div>
    </div>
  );
}

export default function Configuracoes() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  return (
    <div className="max-w-xl">
      <PageHeader title="Configurações" />
      <NotificacoesCelular />
      <Preferencias />
      <div className="bg-card rounded-2xl border border-black/5 overflow-hidden shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
        <div className="p-5 flex items-center gap-3 border-b border-black/5">
          <Avatar name={user?.name} src={user?.avatar} size={48} />
          <div><div className="font-semibold text-navy">{user?.name}</div><div className="text-xs text-muted-foreground">{roleLabel(user?.role)} · {user?.email}</div></div>
        </div>
        <div className="divide-y divide-black/5">
          <Row icon={User} label="Meu perfil" sub="Nome, telefone e foto" onClick={() => navigate("/perfil")} />
          {user?.role === "admin" && <Row icon={ShieldCheck} label="Usuários" sub="Gerenciar usuários e acessos" onClick={() => navigate("/ti")} />}
          <Row icon={Building2} label="Sobre o MPires Connect" sub={`Versão ${APP_VERSION} · Empresa associada Allia`} onClick={() => {}} />
          <Row icon={LogOut} label="Sair da conta" onClick={logout} danger />
        </div>
      </div>
      <p className="text-center text-xs text-slate-400 mt-6">MPires Connect {APP_VERSION}</p>
    </div>
  );
}
